import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Paper,
  Avatar,
  CssBaseline,
} from '@mui/material';
import LockResetIcon from '@mui/icons-material/LockReset';
import { toast } from 'react-toastify';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3002/';

/**
 * Public page — AsroTrack-style set / reset password from email link.
 * URL: /reset-password?token=...&email=...
 */
const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const e = searchParams.get('email');
    const t = searchParams.get('token') || searchParams.get('code');
    if (e) setEmail(decodeURIComponent(e));
    if (t) setToken(decodeURIComponent(t));
  }, [searchParams]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!token || !email || !newPassword || !confirmPassword) {
      setError('Token, email, and both password fields are required');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('New password and confirm password do not match');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}synctrack/user/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          token,
          email,
          newPassword,
          confirmPassword,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data.error || data.message || `Failed (${res.status})`);
      }
      toast.success('Your password has been set successfully.');
      navigate('/');
    } catch (err) {
      setError(err.message || 'Failed to set password');
      toast.error(err.message || 'Failed to set password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <CssBaseline />
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Avatar sx={{ m: 1, bgcolor: 'primary.main' }}>
          <LockResetIcon />
        </Avatar>
        <Typography component="h1" variant="h5" sx={{ mb: 2 }}>
          Set Your Password
        </Typography>
        <Paper elevation={2} sx={{ p: 3, width: '100%' }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Box component="form" onSubmit={onSubmit}>
            <TextField
              margin="normal"
              required
              fullWidth
              label="Email"
              value={email}
              onChange={(ev) => setEmail(ev.target.value)}
              autoComplete="email"
            />
            <TextField
              margin="normal"
              required
              fullWidth
              label="New password"
              type="password"
              value={newPassword}
              onChange={(ev) => setNewPassword(ev.target.value)}
              autoComplete="new-password"
              helperText="8+ chars with upper, lower, number, and special character"
            />
            <TextField
              margin="normal"
              required
              fullWidth
              label="Confirm password"
              type="password"
              value={confirmPassword}
              onChange={(ev) => setConfirmPassword(ev.target.value)}
              autoComplete="new-password"
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading || !token}
              sx={{ mt: 3, mb: 1 }}
            >
              {loading ? 'Saving…' : 'Set Password'}
            </Button>
            {!token && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                Missing reset token. Open the link from your welcome email.
              </Alert>
            )}
            <Button fullWidth sx={{ mt: 1 }} onClick={() => navigate('/')}>
              Back to login
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default ResetPassword;
